/**
 * CorrectionModal - Karten-basiertes Korrektur-Modal fuer Worker
 *
 * Zeigt die aktuelle Erfassung als tippbare Zeilen.
 * Worker tippt auf den falschen Wert → bekommt den gleichen Selektor
 * wie bei der Erfassung (Volltextsuche, Button-Grid, Number-Input).
 *
 * Kaskaden-Logik (FL):
 *   Strecke geaendert → CaptureConfig laden → abhaengige Felder invalidieren
 *   Waescheklasse geaendert → Artikelgruppe invalidieren (wenn noetig)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { RUECKFRAGE_AB_KG } from './GewichtRueckfrage';
import {
  X,
  AlertTriangle,
  Check,
  Loader2,
  Search,
  Route,
  Layers,
  Building2,
  Scale,
  WashingMachine,
  ListChecks,
  ChevronDown,
  ChevronUp,
  Undo2,
  Clock,
} from 'lucide-react';
import {
  WsaEntryResponse,
  ProcessingEntryResponse,
  CorrectionTargetType,
  createCorrectionRequestV2,
  createCorrectionRequestV2Direct,
  ApiException,
} from '../api/client';

// ============================================================================
// Types
// ============================================================================

interface CorrectionModalProps {
  entry: WsaEntryResponse | ProcessingEntryResponse;
  targetType: CorrectionTargetType;
  stationId: string | null;
  onClose: () => void;
  onSuccess: (targetId: string) => void;
}

interface FieldChange {
  field: string;
  oldValue: unknown;
  oldLabel: string;
  newValue: unknown;
  newLabel: string;
}

interface Line {
  id: string;
  name: string;
  subLines?: SubLine[];
}

interface SubLine {
  id: string;
  name: string;
}

interface Customer {
  id: string;
  name: string;
  shortName: string | null;
  itemType?: 'customer' | 'group';
}

interface CustomerGroup {
  id: string;
  name: string;
}

interface Machine {
  id: string;
  name: string;
}

interface Program {
  id: string;
  number: string;
  name: string;
}

interface LaundryClassEntity {
  id: string;
  code: string;
  name: string;
  requiresArticleGroup?: boolean;
  allowedArticleGroups?: ArticleGroupEntity[];
}

interface ArticleGroupEntity {
  id: string;
  code: string;
  name: string;
}

interface CaptureConfig {
  id: string;
  screen: 'FL' | 'WSA';
  lineId: string | null;
  requiresLaundryClass: boolean;
  requiresArticleGroup: boolean;
  allowedLaundryClasses: LaundryClassEntity[];
  allowedArticleGroups: ArticleGroupEntity[];
}

// Grund-Optionen
const FELD_TITEL: Record<string, string> = {
  lineId: 'Strecke',
  subLineId: 'Substrecke',
  customerId: 'Kunde',
  customerGroupId: 'Kundengruppe',
  weightKg: 'Gewicht',
  machineId: 'Maschine',
  programId: 'Programm',
  laundryClassId: 'Wäscheklasse',
  articleGroupId: 'Artikelgruppe',
  shiftDate: 'Schichtdatum',
  shiftTypeCodeSnap: 'Schicht',
};

const REASON_OPTIONS = [
  // K1 Korrektur-Umbau: 'Kein Grund angeben' ist weg — der Grund ist Pflicht,
  // die Pruefung braucht das Warum. Eine Auswahl genuegt.
  { value: '', label: 'Grund wählen … (Pflicht)' },
  { value: 'VERWECHSLUNG', label: 'Verwechslung' },
  { value: 'FALSCH_ABGELESEN', label: 'Falsch abgelesen' },
  { value: 'TIPPFEHLER', label: 'Tippfehler' },
  { value: 'NACHGEWOGEN', label: 'Nachgewogen' },
  { value: 'FALSCHER_WAGEN', label: 'Falscher Wagen' },
  { value: 'SONSTIGES', label: 'Sonstiges...' },
];

// ============================================================================
// Helper: Typ-Guard fuer ProcessingEntryResponse
// ============================================================================

function isProcessingEntry(
  entry: WsaEntryResponse | ProcessingEntryResponse,
): entry is ProcessingEntryResponse {
  return 'lineId' in entry;
}

function isWsaEntry(entry: WsaEntryResponse | ProcessingEntryResponse): entry is WsaEntryResponse {
  return 'machineId' in entry;
}

// ============================================================================
// Sub-Component: FieldRow (tippbare Zeile)
// ============================================================================

function FieldRow({
  label,
  icon: Icon,
  currentLabel,
  change,
  isActive,
  isInvalidated,
  onClick,
  onReset,
}: {
  label: string;
  icon: React.ElementType;
  currentLabel: string;
  change?: FieldChange;
  isActive: boolean;
  isInvalidated: boolean;
  onClick: () => void;
  onReset?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors border-b last:border-b-0 ${
        isActive
          ? 'bg-akzent-tief border-l-4 border-l-blue-500'
          : isInvalidated
            ? 'bg-flaeche-2 border-l-4 border-l-red-400'
            : change
              ? 'bg-flaeche-2 border-l-4 border-l-orange-400'
              : 'hover:bg-flaeche-2 border-l-4 border-l-transparent'
      }`}
      style={{ minHeight: 56 }}
    >
      <Icon
        className={`w-5 h-5 flex-shrink-0 ${
          isInvalidated ? 'text-stoer' : change ? 'text-stand' : 'text-tinte-3'
        }`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-tinte-3 mb-0.5">{label}</div>
        {isInvalidated ? (
          <div className="text-sm font-medium text-stoer">bitte neu waehlen</div>
        ) : change ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-tinte-3 line-through">{change.oldLabel}</span>
            <span className="text-sm text-tinte-3">&rarr;</span>
            <span className="text-sm font-semibold text-stoer">{change.newLabel}</span>
          </div>
        ) : (
          <div className="text-sm font-medium text-tinte truncate">{currentLabel}</div>
        )}
      </div>
      {change && onReset && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onReset();
          }}
          className="p-1 text-tinte-3 hover:text-stoer hover:bg-flaeche-2 rounded"
          title="Zuruecksetzen"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      {!change && !isInvalidated && <ChevronDown className="w-4 h-4 text-tinte-3 flex-shrink-0" />}
    </button>
  );
}

// ============================================================================
// Sub-Component: CustomerSearchEditor
// ============================================================================

function CustomerSearchEditor({
  customers,
  customerGroups,
  topUsage,
  onSelect,
}: {
  customers: Customer[];
  customerGroups: CustomerGroup[];
  topUsage: Array<{ id: string; displayName: string; kind: 'customer' | 'group' }>;
  onSelect: (id: string, label: string) => void;
}) {
  const [search, setSearch] = useState('');

  const filteredResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const matchingGroups = customerGroups
      .filter((g) => g.name.toLowerCase().includes(q))
      .map((g) => ({
        id: `group:${g.id}`,
        name: g.name,
        shortName: null as string | null,
        itemType: 'group' as const,
      }));
    const matchingCustomers = customers
      .filter((c) => c.name.toLowerCase().includes(q) || c.shortName?.toLowerCase().includes(q))
      .map((c) => ({ ...c, itemType: (c.itemType || 'customer') as 'customer' | 'group' }));
    return [...matchingGroups, ...matchingCustomers]
      .sort((a, b) => a.name.localeCompare(b.name, 'de'))
      .slice(0, 50);
  }, [customers, customerGroups, search]);

  return (
    <div className="p-3 space-y-3 bg-flaeche-2 border-t">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tinte-3" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kunde suchen..."
          className="w-full pl-9 pr-4 py-2.5 text-sm border border-linie-2 rounded-lg focus:ring-2 focus:ring-stand focus:border-stand"
          autoFocus
        />
      </div>

      {!search.trim() && topUsage.length > 0 && (
        <div>
          <div className="flex items-center gap-1 text-xs text-tinte-3 mb-1.5">
            <Clock className="w-3 h-3" />
            Schnellwahl
          </div>
          <div className="grid grid-cols-2 gap-2">
            {topUsage.slice(0, 8).map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item.id, item.displayName)}
                className={`min-h-[44px] text-sm font-medium rounded-lg transition-colors px-2 ${
                  item.kind === 'group'
                    ? 'bg-flaeche-2 border border-linie hover:bg-flaeche-2'
                    : 'bg-flaeche-2 border border-linie hover:bg-flaeche-2'
                }`}
              >
                {item.displayName}
              </button>
            ))}
          </div>
        </div>
      )}

      {search.trim() && (
        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
          {filteredResults.length === 0 ? (
            <p className="col-span-full text-center text-tinte-3 py-3 text-sm">
              Keine Treffer fuer &quot;{search}&quot;
            </p>
          ) : (
            filteredResults.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onSelect(c.id, c.shortName || c.name)}
                className={`min-h-[44px] text-sm font-medium rounded-lg transition-colors px-2 ${
                  c.itemType === 'group'
                    ? 'bg-flaeche-2 border border-linie hover:bg-flaeche-2'
                    : 'bg-akzent-tief border border-akzent-tief hover:bg-akzent-tief'
                }`}
              >
                {c.shortName || c.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sub-Component: ButtonGridEditor (Strecke, Maschine, Programm, etc.)
// ============================================================================

function ButtonGridEditor<T extends { id: string; name?: string }>({
  items,
  onSelect,
  renderLabel,
}: {
  items: T[];
  onSelect: (item: T) => void;
  renderLabel?: (item: T) => string;
}) {
  const getLabel = renderLabel || ((item: T) => (item as T & { name: string }).name);
  return (
    <div className="p-3 bg-flaeche-2 border-t">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
        {items.length === 0 ? (
          <p className="col-span-full text-center text-tinte-3 py-3 text-sm">
            Keine Optionen verfuegbar
          </p>
        ) : (
          items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item)}
              className="min-h-[44px] text-sm font-medium border border-linie rounded-lg hover:bg-akzent-tief hover:border-akzent-tief transition-colors px-2"
            >
              {getLabel(item)}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Sub-Component: WeightEditor
// ============================================================================

function WeightEditor({
  currentWeight,
  onSelect,
}: {
  currentWeight: number;
  onSelect: (weight: number) => void;
}) {
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    const num = parseFloat(value);
    if (!isNaN(num) && num > 0) {
      onSelect(num);
    }
  };

  return (
    <div className="p-3 bg-flaeche-2 border-t space-y-2">
      <div className="text-xs text-tinte-3">
        Aktuell: {currentWeight.toLocaleString('de-DE')} kg
      </div>
      {parseFloat(value) >= RUECKFRAGE_AB_KG && (
        <div className="text-xs text-stand bg-flaeche-2 border border-linie rounded px-2 py-1.5">
          Ungewöhnlich hoch — bitte den Wert prüfen, bevor die Korrektur abgeschickt wird.
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="number"
          step="0.1"
          min="0"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
          placeholder="Richtiges Gewicht (kg)"
          className="flex-1 px-3 py-2.5 text-sm border border-linie-2 rounded-lg focus:ring-2 focus:ring-stand focus:border-stand"
          autoFocus
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!value || parseFloat(value) <= 0}
          className="px-4 py-2.5 text-sm bg-stand text-akzent-satz rounded-lg hover:bg-stand disabled:bg-linie-2 disabled:cursor-not-allowed transition-colors"
        >
          <Check className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function CorrectionModal({
  entry,
  targetType,
  stationId,
  onClose,
  onSuccess,
}: CorrectionModalProps) {
  // State
  const [changes, setChanges] = useState<FieldChange[]>([]);
  const [activeEditor, setActiveEditor] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Stammdaten (lazy loaded)
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [topUsage, setTopUsage] = useState<
    Array<{ id: string; displayName: string; kind: 'customer' | 'group' }>
  >([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [allLaundryClasses, setAllLaundryClasses] = useState<LaundryClassEntity[]>([]);
  const [allArticleGroups, setAllArticleGroups] = useState<ArticleGroupEntity[]>([]);
  const [captureConfig, setCaptureConfig] = useState<CaptureConfig | null>(null);
  const [masterDataLoaded, setMasterDataLoaded] = useState(false);
  const [masterDataLoading, setMasterDataLoading] = useState(false);

  // Stammdaten laden (einmalig)
  const loadMasterData = useCallback(async () => {
    if (masterDataLoaded || masterDataLoading) return;
    setMasterDataLoading(true);
    try {
      const fetches: Promise<Response>[] = [
        fetch('/api/data/customers', { credentials: 'include' }),
        fetch('/api/data/customer-groups', { credentials: 'include' }),
        fetch(`/api/data/usage/top?stationId=${stationId}&limit=12`, { credentials: 'include' }),
      ];

      if (targetType === 'PROCESSING_ENTRY') {
        fetches.push(
          fetch('/api/data/lines', { credentials: 'include' }),
          fetch('/api/data/laundry-classes', { credentials: 'include' }),
          fetch('/api/data/article-groups', { credentials: 'include' }),
        );
        // CaptureConfig fuer aktuelle Strecke
        const pe = entry as ProcessingEntryResponse;
        if (pe.lineId) {
          fetches.push(
            fetch(`/api/data/capture-config?screen=FL&lineId=${pe.lineId}`, {
              credentials: 'include',
            }),
          );
        }
      } else {
        // WSA: Maschinen + Programme (gleiche Endpoints wie WsaWizard)
        fetches.push(
          fetch('/api/data/machines', { credentials: 'include' }),
          fetch('/api/data/programs', { credentials: 'include' }),
        );
      }

      const responses = await Promise.all(fetches);

      // Customers
      if (responses[0].ok) setCustomers(await responses[0].json());
      if (responses[1].ok) setCustomerGroups(await responses[1].json());
      if (responses[2].ok) setTopUsage(await responses[2].json());

      if (targetType === 'PROCESSING_ENTRY') {
        if (responses[3]?.ok) setLines(await responses[3].json());
        if (responses[4]?.ok) setAllLaundryClasses(await responses[4].json());
        if (responses[5]?.ok) setAllArticleGroups(await responses[5].json());
        if (responses[6]?.ok) {
          const data = await responses[6].json();
          setCaptureConfig(data.config || null);
        }
      } else {
        // WSA: Maschinen + Programme
        if (responses[3]?.ok) setMachines(await responses[3].json());
        if (responses[4]?.ok) setPrograms(await responses[4].json());
      }
    } catch (err) {
      console.error('Fehler beim Laden der Stammdaten:', err);
    } finally {
      setMasterDataLoaded(true);
      setMasterDataLoading(false);
    }
  }, [masterDataLoaded, masterDataLoading, stationId, targetType, entry]);

  // Stammdaten beim ersten Editor-Oeffnen laden
  const openEditor = useCallback(
    (field: string) => {
      loadMasterData();
      setActiveEditor((prev) => (prev === field ? null : field));
    },
    [loadMasterData],
  );

  // CaptureConfig fuer neue Strecke laden (Kaskade)
  const loadCaptureConfigForLine = useCallback(async (lineId: string) => {
    try {
      const res = await fetch(`/api/data/capture-config?screen=FL&lineId=${lineId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setCaptureConfig(data.config || null);
        return data.config as CaptureConfig | null;
      }
    } catch {
      /* ignore */
    }
    setCaptureConfig(null);
    return null;
  }, []);

  // Change anwenden
  const applyChange = useCallback(
    (field: string, oldValue: unknown, oldLabel: string, newValue: unknown, newLabel: string) => {
      setChanges((prev) => {
        const filtered = prev.filter((c) => c.field !== field);
        return [...filtered, { field, oldValue, oldLabel, newValue, newLabel }];
      });
      // Invalidierung entfernen wenn Feld ausgefuellt
      setInvalidFields((prev) => {
        const next = new Set(prev);
        next.delete(field);
        return next;
      });
      setActiveEditor(null);
    },
    [],
  );

  // Change zuruecksetzen
  const resetChange = useCallback((field: string) => {
    setChanges((prev) => prev.filter((c) => c.field !== field));
  }, []);

  // Kaskade: Strecke aendern → abhaengige Felder invalidieren
  const handleLineChange = useCallback(
    async (line: Line) => {
      const pe = entry as ProcessingEntryResponse;
      const oldLabel = pe.line.name + (pe.subLine ? ` - ${pe.subLine.name}` : '');

      applyChange('lineId', pe.lineId, pe.line.name, line.id, line.name);

      // SubLine zuruecksetzen
      setChanges((prev) => prev.filter((c) => c.field !== 'subLineId'));

      // CaptureConfig fuer neue Strecke laden
      const config = await loadCaptureConfigForLine(line.id);

      // Abhaengige Felder: invalidieren ODER entfernen je nach neuer Config
      const fieldsToInvalidate = new Set<string>();
      const fieldsToRemove = new Set<string>();

      // Wenn neue Strecke Sub-Strecken hat → invalidieren
      if (line.subLines && line.subLines.length > 0) {
        fieldsToInvalidate.add('subLineId');
      } else {
        fieldsToRemove.add('subLineId');
      }

      // Waescheklasse
      if (config?.requiresLaundryClass) {
        // Neue Strecke braucht Waescheklasse → invalidieren
        fieldsToInvalidate.add('laundryClassId');
        if (config?.requiresArticleGroup) {
          fieldsToInvalidate.add('articleGroupId');
        } else {
          fieldsToRemove.add('articleGroupId');
        }
      } else {
        // Neue Strecke braucht KEINE Waescheklasse → Felder komplett entfernen
        fieldsToRemove.add('laundryClassId');
        fieldsToRemove.add('articleGroupId');
      }

      // Bestehende Aenderungen an invalidierten/entfernten Feldern loeschen
      const allAffected = new Set([...fieldsToInvalidate, ...fieldsToRemove]);
      setChanges((prev) => prev.filter((c) => !allAffected.has(c.field)));
      setInvalidFields(fieldsToInvalidate);
      setActiveEditor(null);
    },
    [entry, applyChange, loadCaptureConfigForLine],
  );

  // Kaskade: Waescheklasse aendern → Artikelgruppe invalidieren
  const handleLaundryClassChange = useCallback(
    (lc: LaundryClassEntity) => {
      const pe = entry as ProcessingEntryResponse;
      applyChange('laundryClassId', pe.laundryClassId, pe.laundryClassName || '—', lc.id, lc.name);

      // Artikelgruppe invalidieren wenn noetig
      const lcRecord = captureConfig?.allowedLaundryClasses?.find((a) => a.id === lc.id);
      const effectiveRequiresAG =
        lcRecord?.requiresArticleGroup ?? captureConfig?.requiresArticleGroup ?? false;

      if (effectiveRequiresAG && pe.articleGroupId) {
        setChanges((prev) => prev.filter((c) => c.field !== 'articleGroupId'));
        setInvalidFields((prev) => new Set(prev).add('articleGroupId'));
      } else {
        // Artikelgruppe nicht mehr noetig → Invalidierung entfernen
        setInvalidFields((prev) => {
          const next = new Set(prev);
          next.delete('articleGroupId');
          return next;
        });
      }
      setActiveEditor(null);
    },
    [entry, applyChange, captureConfig],
  );

  // Ein bestätigter alter Wert ist keine Änderung (Meldung #24): Beim
  // Streckenwechsel werden abhängige Felder invalidiert und neu gewählt —
  // wer dabei denselben Wert nimmt, meldet dieses Feld nicht mit. Das Backend
  // überspringt unveränderte Angaben inzwischen ebenfalls; der Filter hier
  // sorgt dafür, dass der Knopf ehrlich anzeigt, ob es etwas zu melden gibt.
  const istEchteAenderung = (c: { field: string; oldValue: unknown; newValue: unknown }) => {
    const neu = c.field === 'weightKg' ? parseFloat(String(c.newValue)) : c.newValue;
    if (c.oldValue == null && neu == null) return false;
    if (c.oldValue == null || neu == null) return true;
    return String(c.oldValue) !== String(neu);
  };

  // Submit
  const handleSubmit = async () => {
    if (changes.length === 0 || invalidFields.size > 0 || submitting) return;

    setSubmitting(true);
    setError('');

    // Baue items aus changes — nur echte Änderungen
    const items = changes.filter(istEchteAenderung).map((c) => ({
      field: c.field,
      newValue: c.field === 'weightKg' ? parseFloat(String(c.newValue)) : c.newValue,
    }));

    if (items.length === 0) {
      setError('Keine Änderung: Alle Angaben entsprechen dem bestehenden Eintrag.');
      setSubmitting(false);
      return;
    }

    // Baue workerComment
    let comment = '';
    if (reason && reason !== 'SONSTIGES') {
      const opt = REASON_OPTIONS.find((o) => o.value === reason);
      comment = opt?.label || '';
    } else if (reason === 'SONSTIGES' && reasonText.trim()) {
      comment = reasonText.trim();
    }

    try {
      const nutzlast = {
        targetType,
        targetId: entry.id,
        items,
        workerComment: comment || undefined,
      };
      // K2 Admin-Direktweg: ohne Station wird sofort gespeichert statt beantragt
      if (stationId) {
        await createCorrectionRequestV2(stationId, nutzlast);
      } else {
        await createCorrectionRequestV2Direct(nutzlast);
      }

      setSuccess('Korrektur gemeldet');
      setTimeout(() => {
        onSuccess(entry.id);
        onClose();
      }, 1200);
    } catch (err) {
      if (err instanceof ApiException) {
        if (err.data.code === 'ALREADY_REPORTED') {
          setError('Fuer diesen Eintrag existiert bereits eine offene Meldung');
          // Badge trotzdem setzen
          onSuccess(entry.id);
        } else {
          setError(err.data.error || 'Fehler beim Melden');
        }
      } else {
        setError('Netzwerkfehler - bitte erneut versuchen');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================================
  // Render: Felder je nach TargetType
  // ============================================================================

  const getChange = (field: string) => changes.find((c) => c.field === field);

  // Processing Entry (FL/Vorarbeiter)
  const renderProcessingFields = () => {
    const pe = entry as ProcessingEntryResponse;
    const customerLabel =
      pe.customer?.shortName || pe.customer?.name || pe.customerGroup?.name || 'Unbekannt';

    // Effektive Strecke (ggf. geaendert)
    const effectiveLineId = (getChange('lineId')?.newValue as string) ?? pe.lineId;
    const effectiveLine = lines.find((l) => l.id === effectiveLineId);

    // CaptureConfig-basierte Felder: Wenn Strecke geaendert, NUR Config entscheidet
    const lineChanged = !!getChange('lineId');
    const showLaundryClass = lineChanged
      ? captureConfig?.requiresLaundryClass
      : pe.laundryClassId || captureConfig?.requiresLaundryClass;
    const showArticleGroup = lineChanged
      ? captureConfig?.requiresArticleGroup
      : pe.articleGroupId || captureConfig?.requiresArticleGroup;

    return (
      <>
        {/* Strecke */}
        <FieldRow
          label="Strecke"
          icon={Route}
          currentLabel={pe.line.name + (pe.subLine ? ` - ${pe.subLine.name}` : '')}
          change={getChange('lineId')}
          isActive={activeEditor === 'lineId'}
          isInvalidated={false}
          onClick={() => openEditor('lineId')}
          onReset={() => {
            resetChange('lineId');
            resetChange('subLineId');
            setInvalidFields(new Set());
            // CaptureConfig zuruecksetzen auf Original
            loadCaptureConfigForLine(pe.lineId);
          }}
        />
        {activeEditor === 'lineId' && masterDataLoaded && (
          <ButtonGridEditor
            items={lines}
            onSelect={(line) => handleLineChange(line)}
            renderLabel={(l) => l.name}
          />
        )}

        {/* Sub-Strecke (nur wenn invalidiert oder geaendert) */}
        {(invalidFields.has('subLineId') || getChange('subLineId')) &&
          effectiveLine?.subLines &&
          effectiveLine.subLines.length > 0 && (
            <>
              <FieldRow
                label="Substrecke"
                icon={Layers}
                currentLabel={pe.subLine?.name || '—'}
                change={getChange('subLineId')}
                isActive={activeEditor === 'subLineId'}
                isInvalidated={invalidFields.has('subLineId')}
                onClick={() => openEditor('subLineId')}
                onReset={() => resetChange('subLineId')}
              />
              {activeEditor === 'subLineId' && effectiveLine.subLines && (
                <ButtonGridEditor
                  items={effectiveLine.subLines}
                  onSelect={(sl) => {
                    applyChange('subLineId', pe.subLineId, pe.subLine?.name || '—', sl.id, sl.name);
                  }}
                  renderLabel={(sl) => sl.name}
                />
              )}
            </>
          )}

        {/* Waescheklasse (wenn vorhanden oder invalidiert) */}
        {(showLaundryClass || invalidFields.has('laundryClassId')) && (
          <>
            <FieldRow
              label="Waescheklasse"
              icon={Layers}
              currentLabel={pe.laundryClassName || '—'}
              change={getChange('laundryClassId')}
              isActive={activeEditor === 'laundryClassId'}
              isInvalidated={invalidFields.has('laundryClassId')}
              onClick={() => openEditor('laundryClassId')}
              onReset={() => resetChange('laundryClassId')}
            />
            {activeEditor === 'laundryClassId' && masterDataLoaded && (
              <ButtonGridEditor
                items={
                  captureConfig?.allowedLaundryClasses?.length
                    ? captureConfig.allowedLaundryClasses
                    : allLaundryClasses
                }
                onSelect={(lc) => handleLaundryClassChange(lc)}
                renderLabel={(lc) => lc.name}
              />
            )}
          </>
        )}

        {/* Artikelgruppe (wenn vorhanden oder invalidiert) */}
        {(showArticleGroup || invalidFields.has('articleGroupId')) && (
          <>
            <FieldRow
              label="Artikelgruppe"
              icon={Layers}
              currentLabel={pe.articleGroupName || '—'}
              change={getChange('articleGroupId')}
              isActive={activeEditor === 'articleGroupId'}
              isInvalidated={invalidFields.has('articleGroupId')}
              onClick={() => openEditor('articleGroupId')}
              onReset={() => resetChange('articleGroupId')}
            />
            {activeEditor === 'articleGroupId' &&
              masterDataLoaded &&
              (() => {
                // Pro-LC Filterung analog FlWizard
                const effectiveLcId =
                  (getChange('laundryClassId')?.newValue as string) ?? pe.laundryClassId;
                const lcRecord = captureConfig?.allowedLaundryClasses?.find(
                  (lc) => lc.id === effectiveLcId,
                );
                const perLcAllowed = lcRecord?.allowedArticleGroups ?? [];
                const globalAllowed = captureConfig?.allowedArticleGroups ?? [];

                let effectiveAllowed: ArticleGroupEntity[];
                if (globalAllowed.length > 0 && perLcAllowed.length > 0) {
                  const perLcIds = new Set(perLcAllowed.map((ag) => ag.id));
                  effectiveAllowed = globalAllowed.filter((ag) => perLcIds.has(ag.id));
                } else if (perLcAllowed.length > 0) {
                  effectiveAllowed = perLcAllowed;
                } else if (globalAllowed.length > 0) {
                  effectiveAllowed = globalAllowed;
                } else {
                  effectiveAllowed = allArticleGroups;
                }

                return (
                  <ButtonGridEditor
                    items={effectiveAllowed}
                    onSelect={(ag) => {
                      applyChange(
                        'articleGroupId',
                        pe.articleGroupId,
                        pe.articleGroupName || '—',
                        ag.id,
                        ag.name,
                      );
                    }}
                    renderLabel={(ag) => ag.name}
                  />
                );
              })()}
          </>
        )}

        {/* Kunde */}
        <FieldRow
          label="Kunde"
          icon={Building2}
          currentLabel={customerLabel}
          change={getChange('customerId')}
          isActive={activeEditor === 'customerId'}
          isInvalidated={false}
          onClick={() => openEditor('customerId')}
          onReset={() => resetChange('customerId')}
        />
        {activeEditor === 'customerId' && masterDataLoaded && (
          <CustomerSearchEditor
            customers={customers}
            customerGroups={customerGroups}
            topUsage={topUsage}
            onSelect={(id, label) => {
              // Entscheide ob customerId oder customerGroupId
              if (id.startsWith('group:')) {
                applyChange('customerGroupId', pe.customerGroupId, customerLabel, id, label);
              } else {
                applyChange('customerId', pe.customerId, customerLabel, id, label);
              }
            }}
          />
        )}

        {/* Gewicht */}
        <FieldRow
          label="Gewicht"
          icon={Scale}
          currentLabel={`${pe.weightKg.toLocaleString('de-DE')} kg`}
          change={getChange('weightKg')}
          isActive={activeEditor === 'weightKg'}
          isInvalidated={false}
          onClick={() => openEditor('weightKg')}
          onReset={() => resetChange('weightKg')}
        />
        {activeEditor === 'weightKg' && (
          <WeightEditor
            currentWeight={pe.weightKg}
            onSelect={(w) => {
              applyChange(
                'weightKg',
                pe.weightKg,
                `${pe.weightKg.toLocaleString('de-DE')} kg`,
                w,
                `${w.toLocaleString('de-DE')} kg`,
              );
            }}
          />
        )}
      </>
    );
  };

  // WSA Entry
  const renderWsaFields = () => {
    const we = entry as WsaEntryResponse;
    const customerLabel =
      we.customer?.shortName || we.customer?.name || we.customerGroup?.name || 'Unbekannt';
    const machineLabel = we.machine?.name || 'Unbekannt';
    const programLabel = we.program ? `${we.program.number} - ${we.program.name}` : '—';

    return (
      <>
        {/* Maschine */}
        <FieldRow
          label="Maschine"
          icon={WashingMachine}
          currentLabel={machineLabel}
          change={getChange('machineId')}
          isActive={activeEditor === 'machineId'}
          isInvalidated={false}
          onClick={() => openEditor('machineId')}
          onReset={() => resetChange('machineId')}
        />
        {activeEditor === 'machineId' && masterDataLoaded && (
          <ButtonGridEditor
            items={machines}
            onSelect={(m) => {
              applyChange('machineId', we.machineId, machineLabel, m.id, m.name);
            }}
            renderLabel={(m) => m.name}
          />
        )}

        {/* Kunde */}
        <FieldRow
          label="Kunde"
          icon={Building2}
          currentLabel={customerLabel}
          change={getChange('customerId')}
          isActive={activeEditor === 'customerId'}
          isInvalidated={false}
          onClick={() => openEditor('customerId')}
          onReset={() => resetChange('customerId')}
        />
        {activeEditor === 'customerId' && masterDataLoaded && (
          <CustomerSearchEditor
            customers={customers}
            customerGroups={customerGroups}
            topUsage={topUsage}
            onSelect={(id, label) => {
              if (id.startsWith('group:')) {
                applyChange('customerGroupId', we.customerGroupId, customerLabel, id, label);
              } else {
                applyChange('customerId', we.customerId, customerLabel, id, label);
              }
            }}
          />
        )}

        {/* Programm */}
        <FieldRow
          label="Programm"
          icon={ListChecks}
          currentLabel={programLabel}
          change={getChange('programId')}
          isActive={activeEditor === 'programId'}
          isInvalidated={false}
          onClick={() => openEditor('programId')}
          onReset={() => resetChange('programId')}
        />
        {activeEditor === 'programId' && masterDataLoaded && (
          <ButtonGridEditor
            items={programs}
            onSelect={(p) => {
              applyChange('programId', we.programId, programLabel, p.id, `${p.number} - ${p.name}`);
            }}
            renderLabel={(p) => `${p.number} - ${p.name}`}
          />
        )}

        {/* Gewicht */}
        <FieldRow
          label="Gewicht"
          icon={Scale}
          currentLabel={`${we.weightKg.toLocaleString('de-DE')} kg`}
          change={getChange('weightKg')}
          isActive={activeEditor === 'weightKg'}
          isInvalidated={false}
          onClick={() => openEditor('weightKg')}
          onReset={() => resetChange('weightKg')}
        />
        {activeEditor === 'weightKg' && (
          <WeightEditor
            currentWeight={we.weightKg}
            onSelect={(w) => {
              applyChange(
                'weightKg',
                we.weightKg,
                `${we.weightKg.toLocaleString('de-DE')} kg`,
                w,
                `${w.toLocaleString('de-DE')} kg`,
              );
            }}
          />
        )}
      </>
    );
  };

  // Submit disabled?
  // K1: ohne Grund kein Absenden; bei 'Sonstiges' braucht es den Freitext
  const grundOk = reason !== '' && (reason !== 'SONSTIGES' || reasonText.trim().length >= 3);
  const echteAenderungen = changes.filter(istEchteAenderung).length;
  const canSubmit =
    changes.some(istEchteAenderung) &&
    invalidFields.size === 0 &&
    !submitting &&
    !success &&
    grundOk;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => !submitting && !success && onClose()}
      />

      {/* Modal */}
      <div className="relative bg-flaeche rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-stand" />
              <h2 className="text-lg font-bold text-tinte">Korrektur vorschlagen</h2>
            </div>
            <button
              type="button"
              onClick={() => !submitting && onClose()}
              className="p-1 text-tinte-3 hover:text-tinte-2 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-tinte-3 mt-1">
            {new Date(entry.recordedAt).toLocaleTimeString('de-DE', {
              timeZone: 'Europe/Berlin',
              hour: '2-digit',
              minute: '2-digit',
            })}{' '}
            Uhr
          </p>
        </div>

        {/* Body - scrollbar */}
        <div className="flex-1 overflow-y-auto">
          {/* Hinweis */}
          {changes.length === 0 && !activeEditor && (
            <div className="px-5 py-3 bg-flaeche-2 border-b">
              <p className="text-sm text-tinte-3">Tippe auf den Wert der nicht stimmt</p>
            </div>
          )}

          {/* Feld-Zeilen */}
          <div>
            {targetType === 'PROCESSING_ENTRY' ? renderProcessingFields() : renderWsaFields()}
          </div>

          {/* Ladeindikator fuer Stammdaten */}
          {masterDataLoading && (
            <div className="px-5 py-3 flex items-center gap-2 text-sm text-tinte-3">
              <Loader2 className="w-4 h-4 animate-spin" />
              Daten werden geladen...
            </div>
          )}

          {/* K2b: Das wird geändert — jede Änderung als Aus-wird-Zeile, einzeln zurücknehmbar */}
          {echteAenderungen > 0 && (
            <div className="px-5 py-3 border-t space-y-1.5">
              <div className="text-sm font-medium text-tinte-2">Das wird geändert</div>
              {changes.filter(istEchteAenderung).map((c) => (
                <div
                  key={c.field}
                  className="flex items-center gap-2 text-sm bg-flaeche-2 border-l-2 border-stand rounded-r px-2.5 py-1.5"
                >
                  <span className="flex-1 min-w-0">
                    <span className="text-tinte-3">{FELD_TITEL[c.field] ?? c.field}: </span>
                    <s className="text-tinte-3">{c.oldLabel}</s>
                    <span className="mx-1 text-tinte-3">→</span>
                    <b className="text-tinte">{c.newLabel}</b>
                  </span>
                  <button
                    type="button"
                    onClick={() => resetChange(c.field)}
                    className="shrink-0 text-xs text-tinte-3 hover:text-tinte-2 underline"
                  >
                    zurücksetzen
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* K2b: Grund als Pflicht-Kacheln — ein Tipp statt Dropdown */}
          {echteAenderungen > 0 && (
            <div className="px-5 py-3 border-t space-y-2">
              <label className="block text-sm font-medium text-tinte-2">
                Warum? <span className="text-stand font-semibold">(Pflicht)</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {REASON_OPTIONS.filter((o) => o.value !== '').map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setReason(opt.value)}
                    className={`px-2 py-2.5 text-sm font-medium rounded-lg border min-h-[44px] transition-colors ${
                      reason === opt.value
                        ? 'border-stand bg-flaeche-2 text-stoer'
                        : 'border-linie-2 bg-flaeche text-tinte-2 hover:border-stand'
                    }`}
                  >
                    {opt.label.replace('...', '')}
                  </button>
                ))}
              </div>
              {reason === 'SONSTIGES' && (
                <textarea
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder="Was ist passiert?"
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-linie-2 rounded-lg focus:ring-2 focus:ring-stand focus:border-stand resize-none"
                />
              )}
              {!grundOk && (
                <p className="text-xs text-tinte-3">
                  Ohne Grund lässt sich nichts absenden — die Prüfung braucht das Warum.
                </p>
              )}
            </div>
          )}

          {/* Error/Success */}
          {error && (
            <div className="mx-5 my-3 p-3 bg-flaeche-2 border border-linie rounded-lg text-sm text-stoer">
              {error}
            </div>
          )}
          {success && (
            <div className="mx-5 my-3 p-3 bg-flaeche-2 border border-linie rounded-lg text-sm text-lauf flex items-center gap-2">
              <Check className="w-4 h-4" />
              {success}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t bg-flaeche-2 rounded-b-xl flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 border border-linie-2 rounded-lg text-tinte-2 hover:bg-flaeche-2 transition-colors text-sm font-medium"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 px-4 py-2.5 bg-stand text-akzent-satz rounded-lg hover:bg-stand disabled:bg-linie-2 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {stationId ? 'Wird gemeldet…' : 'Wird gespeichert…'}
              </>
            ) : (
              <>
                <Undo2 className="w-4 h-4" />
                {stationId
                  ? echteAenderungen > 1
                    ? `${echteAenderungen} Änderungen melden`
                    : 'Korrektur melden'
                  : echteAenderungen > 1
                    ? `${echteAenderungen} Änderungen speichern`
                    : 'Speichern'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
